import { useEffect, useState, type ReactNode } from "react";
import { AlertTriangle, Check, ImageIcon, Shuffle } from "lucide-react";
import { cn, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@nexus/ui";
import { useTranslation } from "react-i18next";
import {
  BRAND_COLORS,
  translateBrandColorLabel,
} from "@/features/identity-hub/lib/brand-colors";
import {
  contrastWithWhite,
  formatContrastRatio,
  meetsWcagAaWithWhite,
  normalizeHexColor,
} from "@/features/identity-hub/lib/color-contrast";
import { loadLogoColorsFromUrl, type LogoColorCandidate } from "@/features/identity-hub/lib/extract-logo-colors";
import { orgColor } from "@/features/identity-hub/utils/iam.utils";

export type AccentColorSource = "auto" | "logo" | "custom";

export interface AccentColorControlProps {
  orgName: string;
  brandColor: string | null;
  onBrandColorChange: (color: string | null) => void;
  initialBrandColor?: string | null;
  logoUrl?: string | null;
  logoFileName?: string;
  disabled?: boolean;
  /** Compact inline control for org settings; stacked section for create branding step. */
  layout?: "compact" | "stacked";
  showSectionHeading?: boolean;
}

function inferSource(brandColor: string | null): AccentColorSource {
  if (brandColor == null) return "auto";
  return "custom";
}

function logoCaptionFromUrl(url: string): string {
  try {
    const pathname = new URL(url, window.location.origin).pathname;
    const segment = pathname.split("/").filter(Boolean).pop();
    return segment ?? "logo";
  } catch {
    return "logo";
  }
}

function ContrastBadge({ accentHex }: { accentHex: string }) {
  const { t } = useTranslation();
  const ratio = contrastWithWhite(accentHex);
  if (ratio == null) return null;
  const passes = meetsWcagAaWithWhite(accentHex);

  return (
    <div className="flex flex-col items-end gap-1">
      <div
        className={cn(
          "flex h-6 items-center gap-1 rounded-full px-2 text-[11px] font-medium leading-none",
          passes
            ? "bg-success-subtle text-success-subtle-foreground"
            : "bg-warning-subtle text-warning-subtle-foreground",
        )}
      >
        {passes ? (
          <Check className="size-3 shrink-0" aria-hidden />
        ) : (
          <AlertTriangle className="size-3 shrink-0" aria-hidden />
        )}
        <span>
          {t("iam.accentColor.contrastWithWhite", "{{ratio}} with white", {
            ratio: formatContrastRatio(ratio),
          })}
        </span>
      </div>
      <span className="text-[11px] leading-snug text-muted-foreground">
        {passes
          ? t("iam.accentColor.initialsReadable", "Initials stay readable")
          : t("iam.accentColor.initialsLowContrast", "Initials may be hard to read")}
      </span>
    </div>
  );
}

function ContrastInline({ accentHex }: { accentHex: string }) {
  const { t } = useTranslation();
  const ratio = contrastWithWhite(accentHex);
  if (ratio == null) return null;
  const passes = meetsWcagAaWithWhite(accentHex);
  return (
    <span
      className={cn(
        "ml-2 text-[11px] leading-snug",
        passes ? "text-success-subtle-foreground" : "text-warning-subtle-foreground",
      )}
    >
      {t("iam.accentColor.contrastInline", "{{ratio}} with white · {{status}}", {
        ratio: formatContrastRatio(ratio),
        status: passes
          ? t("iam.accentColor.readable", "readable")
          : t("iam.accentColor.lowContrast", "low contrast"),
      })}
    </span>
  );
}

export function AccentColorControl({
  orgName,
  brandColor,
  onBrandColorChange,
  initialBrandColor = brandColor,
  logoUrl,
  logoFileName,
  disabled = false,
  layout = "compact",
  showSectionHeading = false,
}: AccentColorControlProps) {
  const { t } = useTranslation();
  const [source, setSource] = useState<AccentColorSource>(() => inferSource(brandColor));
  const [logoCandidates, setLogoCandidates] = useState<LogoColorCandidate[]>([]);
  const [logoLoading, setLogoLoading] = useState(false);
  const [hexDraft, setHexDraft] = useState(() => brandColor ?? "");

  const autoAccent = orgColor(orgName);
  const effectiveAccent = brandColor ?? autoAccent;
  const isDirty = (brandColor ?? null) !== (initialBrandColor ?? null);
  const showChangedFromAuto = isDirty && (initialBrandColor ?? null) === null;

  const logoCaption = logoFileName ?? (logoUrl ? logoCaptionFromUrl(logoUrl) : null);
  const logoSourceDisabled = disabled || !logoUrl || logoLoading || logoCandidates.length === 0;

  useEffect(() => {
    setHexDraft(brandColor ?? "");
  }, [brandColor]);

  useEffect(() => {
    if (!logoUrl) {
      setLogoCandidates([]);
      return;
    }
    let cancelled = false;
    setLogoLoading(true);
    void loadLogoColorsFromUrl(logoUrl).then((colors) => {
      if (cancelled) return;
      setLogoCandidates(colors);
      setLogoLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [logoUrl]);

  useEffect(() => {
    if (source === "logo" && logoCandidates.length === 0 && !logoLoading) {
      if (brandColor == null) setSource("auto");
      else setSource("custom");
    }
  }, [source, logoCandidates.length, logoLoading, brandColor]);

  useEffect(() => {
    if (source !== "logo" || logoCandidates.length === 0) return;
    const current = brandColor?.toUpperCase() ?? null;
    const inList = logoCandidates.some((c) => c.hex.toUpperCase() === current);
    if (!inList) onBrandColorChange(logoCandidates[0]!.hex);
  }, [source, logoCandidates, brandColor, onBrandColorChange]);

  const handleSourceChange = (next: string) => {
    if (!next) return;
    const value = next as AccentColorSource;
    setSource(value);
    if (value === "auto") {
      onBrandColorChange(null);
      return;
    }
    if (value === "logo") {
      const first = logoCandidates[0]?.hex;
      if (first) onBrandColorChange(first);
      return;
    }
    if (value === "custom") {
      const preset = brandColor ?? BRAND_COLORS[0]?.value ?? null;
      onBrandColorChange(preset);
      setHexDraft(preset ?? "");
    }
  };

  const commitHexDraft = () => {
    const normalized = normalizeHexColor(hexDraft);
    if (normalized) {
      onBrandColorChange(normalized);
      setHexDraft(normalized);
    } else {
      setHexDraft(brandColor ?? "");
    }
  };

  const sourceOptions: Array<{
    value: AccentColorSource;
    label: string;
    icon: ReactNode;
    isDisabled: boolean;
    tooltip: string;
  }> = [
    {
      value: "logo",
      label: t("iam.accentColor.sourceLogo", "From logo"),
      icon: <ImageIcon className="size-3.5 shrink-0" />,
      isDisabled: logoSourceDisabled,
      tooltip: logoUrl
        ? t("iam.accentColor.sourceLogoTooltip", "Uses one of the colors found in the uploaded logo.")
        : t("iam.accentColor.sourceLogoNoLogoTooltip", "Upload a logo to pick an accent from its colors."),
    },
    {
      value: "auto",
      label: t("iam.accentColor.sourceAutoLong", "Auto from name"),
      icon: <Shuffle className="size-3.5 shrink-0" />,
      isDisabled: disabled,
      tooltip: t(
        "iam.accentColor.sourceAutoTooltip",
        "Generates a color from the organization name. The same name always gets the same color; renaming changes it.",
      ),
    },
    {
      value: "custom",
      label: t("iam.accentColor.sourceCustom", "Preset or hex"),
      icon: (
        <span
          aria-hidden
          className="size-3.5 shrink-0 rounded-full bg-[conic-gradient(#e60000_0%,#f2b33d_17%,#6ba84f_33%,#3d9da8_50%,#4f7dbf_67%,#7c5cb0_83%,#e60000_100%)]"
        />
      ),
      isDisabled: disabled,
      tooltip: t("iam.accentColor.sourceCustomTooltip", "Pick one of the preset colors or type any hex value."),
    },
  ];

  const segmented = (
    <TooltipProvider delayDuration={200}>
    <div
      role="radiogroup"
      aria-label={t("iam.accentColor.sourceAria", "Accent color source")}
      className="grid grid-cols-3 gap-1 rounded-md border border-border bg-muted p-1"
    >
      {sourceOptions.map((option) => {
        const isSelected = source === option.value;
        const button = (
          <button
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={option.isDisabled}
            onClick={() => handleSourceChange(option.value)}
            className={cn(
              "flex h-8 items-center justify-center gap-1.5 rounded-sm border text-xs font-medium leading-none transition-colors disabled:cursor-not-allowed disabled:opacity-50",
              isSelected
                ? "border-border bg-background text-primary shadow-sm"
                : "border-transparent text-foreground hover:bg-background/60",
            )}
          >
            {option.icon}
            {option.label}
          </button>
        );
        return (
          <Tooltip key={option.value}>
            <TooltipTrigger asChild>
              {/* Disabled buttons fire no pointer events, so the span keeps the tooltip reachable. */}
              {option.isDisabled ? (
                <span tabIndex={0} className="grid cursor-not-allowed [&>button]:pointer-events-none">
                  {button}
                </span>
              ) : (
                button
              )}
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[240px]">
              {option.tooltip}
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
    </TooltipProvider>
  );

  const presetRow = source === "custom" && (
    <div className="flex flex-wrap items-center gap-1.5">
      {BRAND_COLORS.map((c) => {
        const selected = brandColor?.toUpperCase() === c.value.toUpperCase();
        const label = translateBrandColorLabel(t, c.value, c.label);
        return (
          <button
            key={c.value}
            type="button"
            disabled={disabled}
            aria-label={label}
            aria-pressed={selected}
            onClick={() => {
              onBrandColorChange(c.value);
              setHexDraft(c.value);
            }}
            className={cn(
              "relative size-5 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.12)] transition-shadow disabled:cursor-not-allowed disabled:opacity-50",
              selected && "shadow-[0_0_0_2px_hsl(var(--primary))]",
            )}
            style={{ backgroundColor: c.value }}
          >
            {selected ? (
              <Check className="absolute inset-0 m-auto size-2.5 text-white drop-shadow-sm" />
            ) : null}
          </button>
        );
      })}
      {layout === "compact" ? <ContrastInline accentHex={effectiveAccent} /> : null}
    </div>
  );

  const hexField = source === "custom" && (
    <div className="flex h-8 items-center gap-2 rounded-md border border-input bg-background py-0 pl-1.5 pr-2">
      <span
        aria-hidden
        className="size-5 shrink-0 rounded-sm border border-border/40"
        style={{ backgroundColor: effectiveAccent }}
      />
      <input
        type="text"
        disabled={disabled}
        value={hexDraft}
        onChange={(e) => setHexDraft(e.target.value)}
        onBlur={commitHexDraft}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commitHexDraft();
          }
        }}
        aria-label={t("iam.accentColor.hexInputAria", "Accent hex color")}
        className="min-w-0 flex-1 border-0 bg-transparent font-mono text-xs uppercase text-foreground outline-none placeholder:text-muted-foreground"
        placeholder="#1E3A8A"
      />
      {brandColor && (
        <span className="truncate text-[11px] text-muted-foreground">
          {translateBrandColorLabel(t, brandColor, brandColor)}
        </span>
      )}
    </div>
  );

  const logoPanel = source === "logo" && logoCandidates.length > 0 && (
    <div className="flex flex-col gap-3 rounded-lg border border-border p-3.5">
      {logoCaption ? (
        <p className="text-[11px] leading-snug text-muted-foreground">
          {t("iam.accentColor.logoColorsCaption", "Colors found in {{fileName}}", {
            fileName: logoCaption,
          })}
        </p>
      ) : null}
      <div className="flex items-start gap-4">
        {logoCandidates.map((candidate) => {
          const selected = brandColor?.toUpperCase() === candidate.hex.toUpperCase();
          return (
            <button
              key={candidate.hex}
              type="button"
              disabled={disabled}
              aria-pressed={selected}
              onClick={() => onBrandColorChange(candidate.hex)}
              className="flex flex-col items-center gap-1.5 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span
                className={cn(
                  "flex size-10 items-center justify-center rounded-md shadow-sm transition-shadow",
                  selected && "shadow-[0_0_0_2px_hsl(var(--background)),0_0_0_4px_hsl(var(--primary))]",
                )}
                style={{ backgroundColor: candidate.hex }}
              >
                {selected ? <Check className="size-4 text-white drop-shadow-sm" /> : null}
              </span>
              <span
                className={cn(
                  "font-mono text-[10px] leading-none",
                  selected ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {candidate.hex}
              </span>
            </button>
          );
        })}
        <div className="flex-1" />
        <ContrastBadge accentHex={effectiveAccent} />
      </div>
    </div>
  );

  const labelRow = (
    <div className="flex items-baseline justify-between gap-2">
      <label className="text-xs font-medium text-foreground">
        {t("iam.accentColor.label", "Accent color")}
      </label>
      {showChangedFromAuto ? (
        <span className="text-[11px] text-warning-subtle-foreground">
          {t("iam.accentColor.changedFromAuto", "Changed · was Auto")}
        </span>
      ) : null}
    </div>
  );

  if (layout === "stacked") {
    return (
      <div className="flex flex-col gap-3">
        {showSectionHeading ? (
          <div className="flex flex-col gap-0.5">
            <span className="text-[13px] font-semibold text-foreground">
              {t("iam.accentColor.sectionTitle", "Accent color")}
            </span>
            <span className="text-xs text-muted-foreground">
              {t(
                "iam.accentColor.sectionHint",
                "Tints the header above, the org card and the avatar. One color, picked from a source.",
              )}
            </span>
          </div>
        ) : (
          labelRow
        )}
        {segmented}
        {logoPanel}
        {source === "custom" ? (
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-3">
              {hexField}
            </div>
            {presetRow}
          </div>
        ) : null}
        {source === "auto" ? (
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <span
              aria-hidden
              className="size-8 rounded-lg border border-border/50"
              style={{ background: autoAccent }}
            />
            {t("iam.accentColor.autoPreview", "Generated from “{{name}}”", { name: orgName })}
            <ContrastInline accentHex={autoAccent} />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {labelRow}
      {segmented}
      {logoPanel}
      {source === "custom" ? (
        <div className="flex flex-wrap items-center gap-3">{hexField}</div>
      ) : null}
      {presetRow}
    </div>
  );
}

/** Resolves display accent for previews outside the control. */
export function resolveAccentColor(orgName: string, brandColor: string | null): string {
  return brandColor ?? orgColor(orgName);
}
